"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLanguage } from "@/components/LanguageContext";
import { ROUTES } from "@/lib/localizedRoutes";
import { brandConfig } from "@/lib/brandConfig";

// Keep the full set for the footer and the links page.
export const menuItems = ROUTES
    .filter(route => route.menu?.showInHeader === true)
    .map(route => ({
        key: route.key,
        label: route.label,
        paths: route.paths,
        href: route.paths.hu,
        showInLinks: route.menu?.showInLinks === true,
    }));

const primaryKeys = ["prices", "private-room", "gift-card", "faq", "booking"];
const primaryItems = primaryKeys.map(key => menuItems.find(item => item.key === key)).filter(Boolean);

export default function MainMenu({ onItemClick, variant = "header" }) {
    const pathname = usePathname();
    const { language } = useLanguage();
    const isHeader = variant === "header";
    const isEnglish = language === "en";
    const items = isHeader ? primaryItems : menuItems;
    const games = [
        { key: "strike-zone", description: isEnglish
            ? "8 courses, 48 magnetic dart throws and 60 minutes of shared fun."
            : "8 pálya, 48 dobás mágneses nyilakkal és 60 perc közös játék." },
        { key: "feed-the-beast", description: isEnglish
            ? "score 1,500 points in strike zone to unlock the secret bonus game."
            : "érj el 1500 pontot a strike zone-ban, és nyisd meg a titkos bónuszjátékot." },
    ];

    return (
        <nav style={{ "--brand-primary": brandConfig.colors.primary }}
            aria-label={isHeader ? (isEnglish ? "main menu" : "főmenü") : (isEnglish ? "footer menu" : "lábléc menü")}
            className={isHeader
                ? "mx-auto flex w-full max-w-[560px] items-center justify-center px-6 py-8 md:py-10"
                : "mt-8 flex flex-col items-center text-white"}>
            <ul className={isHeader ? "flex w-full flex-col items-center gap-5" : "flex flex-col items-center gap-3"}>
                {isHeader && (
                    <li className="w-full text-center">
                        <details className="group">
                            <summary className="cursor-pointer text-[21px] font-bold text-white transition hover:text-[var(--brand-primary)] md:text-[24px]">
                                {isEnglish ? "about the game" : "a játékról"}
                            </summary>
                            <div className="mt-4 grid gap-3 text-left sm:grid-cols-2">
                                {games.map(game => {
                                    const route = menuItems.find(item => item.key === game.key);
                                    return (
                                        <Link key={game.key} href={route.paths[language] || route.paths.hu}
                                            onClick={onItemClick}
                                            className="rounded-[7px] border border-white/20 bg-white/5 p-4 transition hover:border-[var(--brand-primary)]">
                                            <strong className="block text-base font-black text-[var(--brand-primary)]">{route.label[language] || route.label.hu}</strong>
                                            <span className="mt-2 block text-[13px] leading-relaxed text-white/70">{game.description}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </details>
                    </li>
                )}
                {items.map(item => {
                    const href = item.paths[language] || item.paths.hu;
                    const active = pathname === href;
                    const isBooking = isHeader && item.key === "booking";
                    const label = isHeader && item.key === "prices"
                        ? (isEnglish ? "prices" : "árak")
                        : item.label[language] || item.label.hu;
                    return (
                        <li key={item.key} className={isBooking ? "w-full" : undefined}>
                            <Link href={href} onClick={onItemClick} aria-current={active ? "page" : undefined}
                                className={isBooking
                                    ? "button-pulse mx-auto flex min-h-[52px] w-full max-w-xs items-center justify-center rounded-[6px] bg-[var(--brand-primary)] px-6 text-[21px] font-black text-black transition hover:scale-[1.02]"
                                    : isHeader
                                        ? `block text-center text-[21px] leading-tight transition-colors hover:text-[var(--brand-primary)] md:text-[24px] ${active ? "text-[var(--brand-primary)]" : "text-white"}`
                                        : `${item.key === "booking" ? "button-pulse" : ""} block text-center text-[20px] leading-none transition-colors hover:text-[var(--brand-primary)] md:text-[16px] ${active ? "text-[var(--brand-primary)]" : "text-neutral-300"}`}>
                                {label}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}